import { logsModel } from "@/interfaces/zod/logs";
import { z } from "zod";
import { createBaseApi } from "./base.service";
import { RESOURCES } from "./resources";
import {
  Log,
  FindLogsFilters,
  ClientAnalyticsFilters,
  ClientAnalyticsSummary,
  ErrorAnalyticsFilters,
  ErrorAnalyticsSummary,
  AppUsageFilters,
  AppUsageSummary,
  AppUsageUsersFilters,
  AppUsageUsersResponse,
  TrialFunnelFilters,
  TrialFunnelSummary,
} from "@/interfaces/logs";
import { IPaginatedRequest } from "@/interfaces/paginated.interface";
import { entityListTags } from "@/lib/query-cache-tags";

export interface FindLogsPaginatedParams extends Pick<IPaginatedRequest, "page" | "perPage" | "search"> {
  explicitLoginId?: number;
}

export const logsApi = createBaseApi<any>(RESOURCES.LOGS).injectEndpoints({
  endpoints: (build) => ({
    postLog: build.mutation<any, z.infer<typeof logsModel>>({
      query: (body) => ({
        url: `/logs`,
        method: "POST",
        body,
      }),
    }),
    postLogsLote: build.mutation<{ ok: boolean; gravados: number }, { logs: z.infer<typeof logsModel>[] }>({
      query: (body) => ({
        url: `/logs/lote`,
        method: "POST",
        body,
      }),
    }),
    getLogs: build.query<Log[], FindLogsFilters>({
      query: (filters) => ({
        url: `/logs`,
        method: "GET",
        params: filters,
      }),
      providesTags: (result) => entityListTags(RESOURCES.LOGS, result),
    }),
    getLogsPaginated: build.query<any, FindLogsPaginatedParams>({
      query: (params) => ({
        url: `/logs/paginated`,
        method: "GET",
        params,
      }),
      providesTags: (result) => entityListTags(RESOURCES.LOGS, result),
    }),
    getClientAnalytics: build.query<ClientAnalyticsSummary, ClientAnalyticsFilters>({
      query: (params) => ({
        url: `/logs/analytics/client-summary`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: RESOURCES.LOGS, id: "ANALYTICS" }],
    }),
    getErrorAnalytics: build.query<ErrorAnalyticsSummary, ErrorAnalyticsFilters>({
      query: (params) => ({
        url: `/logs/analytics/errors`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: RESOURCES.LOGS, id: "ANALYTICS" }],
    }),
    getAppUsage: build.query<AppUsageSummary, AppUsageFilters | void>({
      query: (params) => ({
        url: `/logs/analytics/app-usage`,
        method: "GET",
        params: { ...(params || {}) },
      }),
      providesTags: [{ type: RESOURCES.LOGS, id: "ANALYTICS" }],
    }),
    getAppUsers: build.query<AppUsageUsersResponse, AppUsageUsersFilters>({
      query: (params) => ({
        url: `/logs/analytics/app-users`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: RESOURCES.LOGS, id: "ANALYTICS" }],
    }),
    getTrialFunnel: build.query<TrialFunnelSummary, TrialFunnelFilters | void>({
      query: (params) => ({
        url: `/logs/analytics/trial-funnel`,
        method: "GET",
        params: { ...(params || {}) },
      }),
      providesTags: [{ type: RESOURCES.LOGS, id: "ANALYTICS" }],
    }),
    deleteLog: build.mutation<{ ok: boolean; Id: string }, string>({
      query: (id) => ({
        url: `/logs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, error) => error ? [] : [{ type: RESOURCES.LOGS }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLogsQuery,
  useGetLogsPaginatedQuery,
  useGetClientAnalyticsQuery,
  useGetErrorAnalyticsQuery,
  useGetAppUsageQuery,
  useGetAppUsersQuery,
  useGetTrialFunnelQuery,
  useDeleteLogMutation,
} = logsApi;
